import { useEffect, useRef, useState } from "react";

/* SPECPAD MDT-9 cold-boot sequence — plays every time the tablet powers on.
   Any key/tap skips. Lines land on a timer; progress bar sweeps with them. */

const BOOT_LINES = [
  "SPECPAD BIOS v9.4.1 — rugged field build",
  "CPU: quad-core ARM … OK",
  "MEM: 16384 MB LPDDR5 … OK",
  "Secure element: CERTIFIED (FIPS 140-2)",
  "LTE link … ACQUIRING … LOCKED (-71 dBm)",
  "GNSS fix … 11 satellites … OK",
  "NC-CAD uplink … handshake … SYNCED",
  "NCIC interface … AUTHORIZED",
  "Bodycam pairing … STANDBY",
  "Loading department configuration …",
  "All systems nominal. Welcome, officer.",
];

export default function BootScreen({ onDone }) {
  const [step, setStep] = useState(0);
  const done = useRef(false);
  const finish = () => { if (!done.current) { done.current = true; onDone(); } };

  useEffect(() => {
    const t = setInterval(() => setStep((s) => {
      if (s >= BOOT_LINES.length) { clearInterval(t); setTimeout(finish, 500); return s; }
      return s + 1;
    }), 300);
    const skip = () => finish();
    window.addEventListener("keydown", skip);
    window.addEventListener("pointerdown", skip);
    return () => { clearInterval(t); window.removeEventListener("keydown", skip); window.removeEventListener("pointerdown", skip); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pct = Math.min(100, Math.round((step / BOOT_LINES.length) * 100));

  return (
    <div data-testid="cad-boot" className="absolute inset-0 z-50 bg-black flex flex-col items-start justify-center px-10 cursor-pointer select-none">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <p className="font-mono text-[10px] tracking-[0.4em] text-zinc-500 uppercase">SPECPAD · MDT-9 · POWER ON</p>
        </div>
        <div className="min-h-[220px]">
          {BOOT_LINES.slice(0, step).map((l, i) => (
            <p key={i} className="font-mono text-[11px] leading-relaxed text-emerald-400/90" style={{ animation: "bootIn 120ms ease-out" }}>
              <span className="text-zinc-600">[{String(i * 297).padStart(5, "0")}ms]</span> {l}
            </p>
          ))}
          {step < BOOT_LINES.length && <span className="inline-block w-2 h-3.5 bg-emerald-400 animate-pulse mt-1" />}
        </div>
        <div className="mt-6 h-1 w-full bg-zinc-800 overflow-hidden">
          <div className="h-full bg-emerald-400 transition-all duration-300" style={{ width: `${pct}%` }} />
        </div>
        <div className="flex items-center justify-between mt-2">
          <p className="font-mono text-[8px] tracking-[0.3em] text-zinc-600 uppercase">{pct}%</p>
          <p className="font-mono text-[8px] tracking-[0.3em] text-zinc-600 uppercase animate-pulse">press any key to skip</p>
        </div>
      </div>
      <style>{`@keyframes bootIn { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: none; } }`}</style>
    </div>
  );
}
